import { Link, Navigate, useSearchParams } from "react-router";
import { useIsAdmin } from "@web/features/auth/useMe.ts";
import { Checkbox } from "@web/components/ui/checkbox.tsx";
import { useUiStore } from "@web/stores/ui.ts";
import { ConnectionStatusPanel } from "./ConnectionStatusPanel.tsx";
import { FieldsManager } from "./FieldsManager.tsx";
import { ResearchCategoriesPanel } from "./ResearchCategoriesPanel.tsx";

/** User-mode settings — status, fields, research categories, debug. Template admin → Workbench. */
export function SettingsPage() {
  const [params] = useSearchParams();
  const tab = params.get("tab");
  const isAdmin = useIsAdmin();
  const debugMode = useUiStore((s) => s.debugMode);
  const setDebugMode = useUiStore((s) => s.setDebugMode);
  const showMetaTab = useUiStore((s) => s.showMetaTab);
  const setShowMetaTab = useUiStore((s) => s.setShowMetaTab);

  // Old bookmarks: Settings → Enrich / Library → workbench (admin only).
  if (isAdmin && (tab === "enrich" || tab === "library")) {
    const next = new URLSearchParams();
    if (tab === "enrich") {
      next.set("tab", "enrich");
      const playlist = params.get("playlist");
      if (playlist) next.set("playlist", playlist);
    }
    // library → Sync (default workbench tab, no ?tab=)
    const qs = next.toString();
    return <Navigate to={qs ? `/workbench?${qs}` : "/workbench"} replace />;
  }

  if (isAdmin && tab === "prompt") {
    return <Navigate to="/workbench?tab=prompt" replace />;
  }

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-line px-5 pt-4 pb-4">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">App</p>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted">
          Connection health, research categories, custom fields, and diagnostics.
        </p>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <div className="mx-auto max-w-3xl space-y-8">
          <ConnectionStatusPanel />

          <section className="rounded-lg border border-line bg-panel px-4 py-4">
            <h2 className="text-sm font-semibold">Account</h2>
            <p className="mt-1 text-sm text-muted">
              Switch Clerk user (e.g. admin vs test) or sign out completely.
            </p>
            <Link
              to="/sign-in"
              className="mt-3 inline-flex text-sm font-semibold text-accent hover:underline"
            >
              Open sign-in page
            </Link>
          </section>

          <ResearchCategoriesPanel />

          <FieldsManager />

          <section className="rounded-lg border border-line bg-panel px-4 py-4">
            <h2 className="text-sm font-semibold">Debug mode</h2>
            <p className="mt-1 text-sm text-muted">
              Shows error codes inline, logs every API call with timing, and a corner
              readout of the last request. Does not change app behaviour.
            </p>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={debugMode}
                onCheckedChange={(v) => setDebugMode(v === true)}
              />
              <span>Enable debug mode</span>
            </label>
          </section>

          <section className="rounded-lg border border-line bg-panel px-4 py-4">
            <h2 className="text-sm font-semibold">Sheet Meta tab</h2>
            <p className="mt-1 text-sm text-muted">
              Adds a Meta tab on track, album, and artist sheets with the pretty
              stored MusicBrainz / AcousticBrainz JSON.
            </p>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={showMetaTab}
                onCheckedChange={(v) => setShowMetaTab(v === true)}
              />
              <span>Show Meta tab</span>
            </label>
          </section>

          {isAdmin && (
            <>
              <section className="rounded-lg border border-line bg-panel px-4 py-4">
                <h2 className="text-sm font-semibold">Research prompt templates</h2>
                <p className="mt-1 text-sm text-muted">
                  Edit the global initial / continue clipboard templates in Workbench. Category
                  angles above are yours (per account).
                </p>
                <Link
                  to="/workbench?tab=prompt"
                  className="mt-3 inline-flex text-sm font-semibold text-accent hover:underline"
                >
                  Open Prompt in Workbench
                </Link>
              </section>

              <section className="rounded-lg border border-line bg-panel px-4 py-4">
                <h2 className="text-sm font-semibold">Workbench</h2>
                <p className="mt-1 text-sm text-muted">
                  Enrichment, coverage, sync jobs, MBID tools, research prompt, and raw JSON
                  — admin only. Not linked in the sidebar.
                </p>
                <Link
                  to="/workbench"
                  className="mt-3 inline-flex text-sm font-semibold text-accent hover:underline"
                >
                  Open workbench
                </Link>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
